<template>
  <v-form class="config-wrapper" ref="form" v-model="isFormValid">
    <v-row v-if="selectedService.gmb_category">
      <v-col cols="12">
        <v-text-field
          :value="selectedService.display_name"
          v-validate="'required|max:120'"
          :data-vv-name="fieldsTranslations.name"
          :error-messages="errors.collect(fieldsTranslations.name)"
          :label="fieldsTranslations.name"
          class="text-field"
          counter="120"
          @input="defaultPayload.display_name = $event"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="6">
        <v-select
          v-model="priceType"
          class="select-box"
          item-text="name"
          return-object
          hide-details
          :items="priceTypes"
          :label="$t('message.facilityDetails.services.price')"
        />
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-model="defaultPayload.price"
          type="number"
          :disabled="isPriceFieldDisabled"
          v-validate="priceValidationRules"
          :data-vv-name="fieldsTranslations.price"
          :error-messages="errors.collect(fieldsTranslations.price)"
          :label="fieldsTranslations.price"
          class="text-field"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="pt-0">
        <v-textarea
          v-model="defaultPayload.description"
          counter="300"
          v-validate="'max:300'"
          :data-vv-name="fieldsTranslations.description"
          :error-messages="errors.collect(fieldsTranslations.description)"
          :label="fieldsTranslations.description"
          rows="3"
          no-resize
          class="text-field"
        ></v-textarea>
      </v-col>
    </v-row>
    <div class="actions">
      <ui-button
        v-if="showDeleteButton"
        icon="icon-trash"
        iconColor="red"
        @click="handleDelete"
      >
        {{ $t("message.facilityDetails.services.deleteService") }}
      </ui-button>
      <div class="actions__button-group">
        <ui-button color="primary" outlined @click="handleClose">
          {{ $t("message.common.cancel") }}
        </ui-button>
        <ui-button color="primary" :disabled="!isFormValid" @click="handleSave">
          {{ $t("message.common.save") }}
        </ui-button>
      </div>
    </div>
  </v-form>
</template>

<script>
import { SERVICE_ACTIONS_ENUM } from "@/enums/gmb-services.enum";
import PayloadManager from "@/helpers/gmbServices/payload-manager";

export default {
  name: "GmbServiceConfig",

  props: {
    selectedService: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isFormValid: false,
      priceType: null,
      defaultPayload: {
        description: null,
        price: null,
        currency_code: null
      },
      priceTypes: [
        { id: 1, name: this.$t("message.facilityDetails.services.noPrice") },
        { id: 2, name: this.$t("message.facilityDetails.services.fixedPrice") }
      ]
    };
  },
  computed: {
    isPriceFieldDisabled() {
      return this.priceType.id === this.priceTypes[0].id;
    },
    localCurrency() {
      return this.defaultPayload.currency_code;
    },
    showDeleteButton() {
      return this.selectedService.id || this.selectedService.selected;
    },
    priceValidationRules() {
      return {
        required: !this.isPriceFieldDisabled,
        numeric: !this.isPriceFieldDisabled
      };
    },
    hasSelectedServicePrice() {
      return !!this.selectedService.price;
    },
    fieldsTranslations() {
      return {
        price: this.$t("message.facilityDetails.services.servicePrice", [
          this.localCurrency
        ]),
        description: this.$t(
          "message.facilityDetails.services.serviceDescription"
        ),
        name: this.$t("message.common.name")
      };
    }
  },

  methods: {
    handleClose() {
      this.$emit("close");
    },
    handleDelete() {
      const payload = PayloadManager.getServiceItemsPayload(
        SERVICE_ACTIONS_ENUM.DELETE,
        this.selectedService
      );
      this.$emit("delete", payload);
    },
    handleSave() {
      this.$validator.validateAll().then(result => {
        if (result) {
          const payload = this.getUpdatePayload();
          this.$emit("update", payload);
        }
      });
    },
    getUpdatePayload() {
      const combinedData = this.getCombinedData();
      const actionType = combinedData.id
        ? SERVICE_ACTIONS_ENUM.UPDATE
        : SERVICE_ACTIONS_ENUM.CREATE;
      const payload = PayloadManager.getServiceItemsPayload(
        actionType,
        combinedData
      );

      return payload;
    },
    getCombinedData() {
      return { ...this.selectedService, ...this.defaultPayload };
    },
    setDefaultPrice() {
      if (this.hasSelectedServicePrice) {
        this.setPriceFromSelectedService();
      } else {
        this.setDefaultCurrencyAndLabel();
      }
    },
    setPriceFromSelectedService() {
      this.priceType = this.priceTypes[1];
      this.defaultPayload.price = this.selectedService.price;
      this.defaultPayload.currency_code = this.selectedService.currency_code;
    },
    setDefaultCurrencyAndLabel() {
      this.defaultPayload.currency_code = this.currency;
      this.priceType = this.priceTypes[0];
    }
  },
  watch: {
    selectedService: {
      handler() {
        this.setDefaultPrice();
        this.defaultPayload.description = this.selectedService.description;
      },
      deep: true,
      immediate: true
    },
    isPriceFieldDisabled: {
      handler(currentValue) {
        if (currentValue) {
          this.defaultPayload.price = null;
        }
      },
      immediate: true
    }
  }
};
</script>
<style lang="scss" scoped>
.config-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 302px;

  & > * {
    flex-grow: 0;
  }
}
::v-deep {
  &.v-list .v-list-item__title {
    @include font-size(14px);
  }
}
.select-box,
.text-field {
  ::v-deep {
    .v-select__selection,
    input,
    label,
    textarea {
      @include font-size(14px);
    }

    position: relative;
    .v-text-field__details {
      .v-messages {
        position: absolute;
        width: 100%;
      }
      .v-counter {
        position: absolute;
        right: 0;
      }
    }
  }
}
.actions {
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $spacing;
  margin-bottom: $spacing * 3;

  &__button-group {
    display: flex;
    gap: $spacing;
    margin-left: auto;
  }
}
</style>
